import axios, {type AxiosRequestConfig, type InternalAxiosRequestConfig} from 'axios';
import {landingRequestConsent} from "analytics/landingRequestConsent";
import type {CheckoutDocumentRead} from "pay/checkoutDocument";
import {getIdToken} from "../auth/authProviders";
import {firebaseIdentityChanged} from "../auth/firebaseIdentityBinding";
import {resolveActiveLocale} from "../i18n/localePreference";
import {
	browserMeasurementHeaders,
	holdSignupChannelReferrer,
	VISIT_START_HEADER,
} from "../attribution/signupChannelHeader";

// This page load's referrer may be the Firebase OAuth handler's rather than the visitor's, and only the
// session bootstrap can tell (ui#2516, kitecorp/transferify-ui#2331 item A) — it settles the verdict once
// `completeRedirectSignIn` has answered. Held HERE rather than there because this module is evaluated as
// the transport is wired, before any api request can be made, while the bootstrap is a React effect behind
// a dynamic import: on a redirect return the account-establishing `GET /account` is issued from inside it
// while `getRedirectResult` is still settling. Measured, not argued — an earlier attempt at this ticket
// recorded the verdict from `completeRedirectSignIn` alone, and the local-stack journey caught that request
// leaving with the Auth emulator's own `referral`. Only the referrer is withheld; a bucket the landing URL
// proves still travels throughout, which is what keeps that same request counted for a paid arrival.
holdSignupChannelReferrer();

interface FirebaseIdentityBinding {
	expectedFirebaseUid?: string;
}

/** Request config for legal records whose payload belongs to one exact Firebase account. */
export type AccountBoundAxiosConfig = AxiosRequestConfig & Required<FirebaseIdentityBinding>;

type RetriableAxiosConfig = InternalAxiosRequestConfig & FirebaseIdentityBinding & {_retry?: boolean};

function requestToken(forceRefresh: boolean, expectedUid?: string): Promise<string | null> {
	return expectedUid === undefined
		? getIdToken(forceRefresh)
		: getIdToken(forceRefresh, expectedUid);
}

/**
 * Auth wiring for axios, Firebase-based.
 *
 * A request interceptor attaches the current Firebase ID token as the Authorization Bearer on
 * every request — the Firebase SDK refreshes the token automatically, so there is no /auth/refresh
 * dance. On a 401 we force a single token refresh and retry, to cover a token that expired in flight.
 *
 * The lightweight `auth/authProviders` facade is imported eagerly because the session bootstrap,
 * login route, and create flow already require it on every app load. Its `firebaseAuthImpl` dependency
 * remains behind the facade's effective dynamic-import boundary, so `@firebase/auth` stays outside
 * the eager bundle while the very first request still awaits and attaches the token.
 */
export default class AxiosUtils {
	/** @deprecated tokens now come from Firebase via the request interceptor below. */
	static setAuthorizationToken(token: string) {
		if (token) {
			axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
		}
	}
}

axios.interceptors.request.use(async (config) => {
	const enforceLandingConsent = landingRequestConsent(config);
	const expectedUid = (config as FirebaseIdentityBinding).expectedFirebaseUid;
	const token = await requestToken(false, expectedUid);
	// A close, account change or locale change during token resolution invalidates the rendered document.
	(config as {checkoutDocumentSelection?: CheckoutDocumentRead}).checkoutDocumentSelection?.();
	// Consent first: it may cancel this request, and a cancelled request must not spend the page load's
	// one visit marker (ui#2805). Both are synchronous, so the check still runs right before Axios sends.
	enforceLandingConsent();
	if (token) {
		config.headers = config.headers ?? {};
		config.headers['Authorization'] = 'Bearer ' + token;
		attachMeasurementHeaders(config);
	}
	return config;
});

/**
 * Tags a token-bearing request with this page load's coarse acquisition channel (api#753), the campaign
 * labels its landing link carried (api#1244), and — on the page load's first such request only — the
 * visit marker (ui#2805).
 *
 * <b>In practice that is EVERY api request of EVERY visitor, signed in or not, and that is deliberate.</b>
 * `App` boots `bootstrapSession`, which calls `ensureAnonymous()` — so a Firebase session exists from
 * hydration onward and `getIdToken(false)` returns a token for a visitor who has never signed in. The
 * `if (token)` above is therefore not an authentication gate in the running app; it only excludes the
 * pre-hydration and SSG cases where no session exists at all.
 *
 * <b>Do not "tighten" this to real sessions only — it would zero the dominant signup path.</b> The
 * email-code flow establishes the account inside `POST /auth/email-code/confirm`, which is `permitAll` on
 * the api: the only bearer it carries is the ANONYMOUS one, so a `token && !isAnonymous` guard would strip
 * the header from exactly the request that establishes. The provider flow is the same shape one step
 * later — the account is provisioned in the api's auth filter, before any controller runs, on whichever
 * call goes first. Which call that is, is a race the browser cannot win in advance, which is the other
 * reason every request carries it rather than one guessed one.
 *
 * Best effort by contract: no answer simply means no header, which the api counts as untracked and as no
 * campaign. Nothing here may reject the config — a measurement that could fail a product request would
 * cost more than it measures.
 */
// `headers` REQUIRED, and no `| any`: the previous `{headers?: Record<string, unknown> | any}` collapsed
// to `any` (a union with `any` is `any`), so it asserted nothing at all and the `?` it advertised was
// never checked before the write below. The caller establishes `headers` on the line before it calls, so
// requiring it states the real contract and keeps the write honestly type-checked.
function attachMeasurementHeaders(config: {headers: Record<string, unknown>}): void {
	// Both answers are derived once per page load and memoized there (ui#1372) — this runs on every
	// outbound request and neither answer can change within a page load. WHICH headers that produces, and
	// which one request carries the visit marker (ui#2805), is `browserMeasurementHeaders`' rule, not this
	// function's; this is only where the transport meets it.
	//
	// The marker is REMOVED before the assign because a 401 retry re-submits the same config, which still
	// carries the first attempt's marker, and `Object.assign` never removes a key. The api counts both
	// attempts, so leaving it would count one page load as two visits.
	delete config.headers[VISIT_START_HEADER];
	Object.assign(config.headers, browserMeasurementHeaders());
}

/**
 * Language wiring for axios (ui#125). Tags every API request with `Accept-Language: <ro|en>` from the
 * active locale so the backend (api#47, live in prod) returns localized error messages in the user's
 * language. This interceptor runs OUTSIDE React, so the locale comes from localStorage/hostname via
 * `resolveActiveLocale()` — the same normalization `useLocale()` uses — not the React context. A
 * request that already sets `Accept-Language` explicitly is left untouched.
 */
axios.interceptors.request.use((config) => {
	config.headers = config.headers ?? {};
	if (!config.headers['Accept-Language']) {
		config.headers['Accept-Language'] = resolveActiveLocale();
	}
	return config;
});

axios.interceptors.response.use(
	(response) => response,
	async (error) => {
		const original = error.config as RetriableAxiosConfig | undefined;
		if (error.response?.status === 401 && original && !original._retry) {
			original._retry = true;
			let token: string | null;
			try {
				// An account-bound legal record can refresh only that same account's credential. Surface
				// identity changes so its caller can release a claim that was not actually authenticated.
				token = await requestToken(true, original.expectedFirebaseUid);
			} catch (refreshError) {
				return Promise.reject(firebaseIdentityChanged(refreshError) ? refreshError : error);
			}
			if (token) {
				original.headers = original.headers ?? {};
				original.headers['Authorization'] = 'Bearer ' + token;
				return axios(original);
			}
		}
		return Promise.reject(error);
	}
);
